import React from 'react';
import Modal from '@lib/components/Modal/Modal';
import Form from '@lib/components/ReactHookForm/FormContainer';
import replaceNullsWithUndefined from '@lib/utils/replaceNullsWithUndefined';
import UserFieldNames from '@lib/enums/fieldNames/userFieldNames';
import { useTranslation } from 'react-i18next';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { ErrorLike } from '@apollo/client';
import { GraphQLFormattedError } from 'graphql';
import { Values } from '@lib/interfaces/form';
import { IconName } from '@lib/components/Modal/enums';
import { ApiToken } from 'graphql-common';
import SupportiveMessage from '@lib/components/SupportiveMessage/SupportiveMessage';
import ClipboardCopy, {
  ClipboardCopyType,
} from '@lib/components/ClipboardCopy/ClipboardCopy';
import Typography from '@lib/components/Typography/Typography';
import getDefaultValuesFromQueryData from 'routes/PublicApi/utils/getDefaultValuesFromQueryData';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import getUserFormFields, {
  defaultValues,
} from '../../utils/getApiTokenFormFields';
import styles from './ApiTokenModalForm.module.scss';

function FormActions(props: {
  isEditForm?: boolean;
  loading?: boolean;
  onCancel: () => void;
}) {
  const { isEditForm, loading, onCancel } = props;
  const { t } = useTranslation();
  return (
    <ActionsRow
      actions={[
        <Button
          buttonText={t('cancel')}
          buttonType={ButtonTypes.secondaryOutlined}
          disabled={loading}
          fullWidth
          leftIcon="close"
          onClick={onCancel}
        />,
        <Button
          buttonText={isEditForm ? t('save-changes') : t('create')}
          buttonType={ButtonTypes.primaryFilled}
          fullWidth
          disabled={loading}
          leftIcon="check"
          type="submit"
        />,
      ]}
    />
  );
}

export default function ApiTokenModalForm(props: {
  createdApiToken: ApiToken | null;
  selectedApiToken: ApiToken | null;
  graphQLErrors?: readonly GraphQLFormattedError[];
  isOpen: boolean;
  loading;
  onClose: () => void;
  onSubmit: (values: Values) => void;
  error?: ErrorLike;
}) {
  const {
    createdApiToken,
    selectedApiToken,
    graphQLErrors,
    isOpen,
    loading,
    onClose,
    onSubmit,
    error,
  } = props;
  const { t } = useTranslation();
  const fields = getUserFormFields({ t });
  const isEdit = selectedApiToken?.id;
  let modalTitle: string | undefined = isEdit
    ? 'edit-api-token-label'
    : 'add-api-token';
  if (!isEdit && createdApiToken) {
    modalTitle = 'api-token-created';
  }
  if (error) {
    modalTitle = undefined;
  }
  let content;
  if (error) {
    content = (
      <ErrorPage
        status={getStatusFromError(error)}
        description={getDescriptionFromError(error)}
      />
    );
  } else if (!isEdit && createdApiToken) {
    content = (
      <div>
        <SupportiveMessage text={t('api-token-copy-desc')} />
        {createdApiToken?.token && (
          <div>
            <Typography variant="body" strong className={styles.title}>
              {t('api-token')}
            </Typography>
            <ClipboardCopy
              copyText={createdApiToken.token}
              type={ClipboardCopyType.CodeBlock}
            >
              {createdApiToken.token}
            </ClipboardCopy>
          </div>
        )}
      </div>
    );
  } else {
    content = (
      <Form
        key={selectedApiToken?.updatedAt}
        formId="api-token"
        defaultValues={replaceNullsWithUndefined(
          isEdit
            ? getDefaultValuesFromQueryData(selectedApiToken)
            : defaultValues,
        )}
        onSubmit={onSubmit}
        fields={fields}
        actionsComponent={FormActions}
        actionsComponentProps={{
          onCancel: onClose,
          isEditForm: isEdit,
        }}
        graphQLErrors={graphQLErrors}
        graphQLErrorsReplacePathMap={{
          username: UserFieldNames.Username,
        }}
        loading={loading}
      />
    );
  }
  return (
    <Modal
      isOpen={isOpen}
      title={modalTitle ? t(modalTitle) : undefined}
      onClose={onClose}
      icon={error ? undefined : IconName.Key}
    >
      {content}
    </Modal>
  );
}
